'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Grid3X3,
  ListFilter,
  Plus,
  Search,
} from 'lucide-react';
import type {
  BlueprintProgramAttentionStatusDto,
  BlueprintProgramLifecycleStateDto,
  BlueprintProgramProjectionDto,
} from '@helix/lib';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { listBlueprintPrograms } from '@/lib/blueprint-runs-api';
import { cn } from '@/lib/cn';

type TypeFilter =
  | 'all'
  | 'value_creation'
  | 'operating_model'
  | 'transformation'
  | 'integration'
  | 'turnaround'
  | 'cost_transformation'
  | 'growth';

type StateFilter = 'all' | BlueprintProgramLifecycleStateDto | BlueprintProgramAttentionStatusDto;
type ViewMode = 'cards' | 'list';

const BLUEPRINT_TYPE_LABELS: Record<Exclude<TypeFilter, 'all'>, string> = {
  value_creation: 'Value Creation',
  operating_model: 'Operating Model',
  transformation: 'Transformation',
  integration: 'Integration',
  turnaround: 'Turnaround',
  cost_transformation: 'Cost Transformation',
  growth: 'Growth',
};

const LIFECYCLE_LABELS: Record<BlueprintProgramLifecycleStateDto, string> = {
  draft: 'Draft',
  synthesizing: 'Synthesizing',
  decision_needed: 'Decision needed',
  approved: 'Approved',
  active: 'Active',
  drifted: 'Drifted',
  closed: 'Closed',
  superseded: 'Superseded',
};

const ATTENTION_LABELS: Record<BlueprintProgramAttentionStatusDto, string> = {
  draft: 'Draft',
  synthesizing: 'Synthesizing',
  decision_needed: 'Decision needed',
  evidence_gap: 'Evidence gap',
  blocked: 'Blocked',
  on_track: 'On track',
  watch: 'Watch',
  drifted: 'Drifted',
  closed: 'Closed',
};

function statusVariant(status: BlueprintProgramAttentionStatusDto): 'keep' | 'defer' | 'eliminate' | 'pending' | 'accent' {
  if (status === 'on_track' || status === 'closed') return 'keep';
  if (status === 'decision_needed' || status === 'watch' || status === 'evidence_gap') return 'defer';
  if (status === 'blocked' || status === 'drifted') return 'eliminate';
  if (status === 'synthesizing') return 'accent';
  return 'pending';
}

function formatDate(value: string | null): string {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not scheduled';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
}

function typeLabel(program: BlueprintProgramProjectionDto): string {
  if (program.blueprintType && program.blueprintType in BLUEPRINT_TYPE_LABELS) {
    return BLUEPRINT_TYPE_LABELS[program.blueprintType as Exclude<TypeFilter, 'all'>];
  }
  return program.packTitle || 'Blueprint';
}

function ownerLabel(program: BlueprintProgramProjectionDto): string {
  return program.ownerUserId ? program.ownerUserId.replaceAll('_', ' ') : 'Owner not assigned';
}

function buildWorkingHref(program: BlueprintProgramProjectionDto): string {
  const params = new URLSearchParams({
    tab: 'run',
    runId: program.currentRunId,
  });
  return `/strategy-studio/strategies/${encodeURIComponent(program.strategyId)}/blueprint-runs?${params.toString()}`;
}

function deriveSignal(programs: BlueprintProgramProjectionDto[]): string {
  const blocking = programs.find((program) => program.attentionStatus === 'blocked' || program.blockingGapCount > 0);
  if (blocking) {
    return `Open ${blocking.title}: ${blocking.blockingGapCount || 1} blocking gap needs resolution before leadership can rely on the recommendation.`;
  }
  const decision = programs.find((program) => program.attentionStatus === 'decision_needed');
  if (decision) {
    return `Open ${decision.title}: decision is ready for review; ${decision.supervisionActionCount} supervision action${decision.supervisionActionCount === 1 ? '' : 's'} already captured.`;
  }
  const drifted = programs.find((program) => program.attentionStatus === 'drifted');
  if (drifted) {
    return `Open ${drifted.title}: monitored outcomes have drifted and the program needs escalation.`;
  }
  if (programs.length > 0) {
    return `${programs.length} Blueprint Program${programs.length === 1 ? '' : 's'} are visible across Strategy Studio. Sort by attention to see what needs work first.`;
  }
  return 'No Blueprint Programs are active yet. Start with a strategic mandate, then launch the first governed blueprint.';
}

function attentionRank(status: BlueprintProgramAttentionStatusDto): number {
  return {
    blocked: 0,
    drifted: 1,
    decision_needed: 2,
    evidence_gap: 3,
    watch: 4,
    synthesizing: 5,
    draft: 6,
    on_track: 7,
    closed: 8,
  }[status];
}

export default function BlueprintPortfolioClient() {
  const [programs, setPrograms] = useState<BlueprintProgramProjectionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    listBlueprintPrograms()
      .then((rows) => {
        if (!alive) return;
        setPrograms(rows);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Unable to load Blueprint Programs.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const filteredPrograms = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return programs
      .filter((program) => {
        if (typeFilter !== 'all' && program.blueprintType !== typeFilter) return false;
        if (
          stateFilter !== 'all' &&
          program.lifecycleState !== stateFilter &&
          program.attentionStatus !== stateFilter
        ) {
          return false;
        }
        if (!normalizedSearch) return true;
        return [
          program.title,
          program.headline,
          program.executiveTakeaway,
          program.packTitle,
          typeLabel(program),
        ]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((left, right) => {
        const attentionDelta = attentionRank(left.attentionStatus) - attentionRank(right.attentionStatus);
        if (attentionDelta !== 0) return attentionDelta;
        return new Date(right.lastUpdatedAt).getTime() - new Date(left.lastUpdatedAt).getTime();
      });
  }, [programs, search, stateFilter, typeFilter]);

  const summary = useMemo(
    () => ({
      active: programs.filter((program) => program.lifecycleState === 'active' || program.lifecycleState === 'drifted').length,
      decisions: programs.filter((program) => program.attentionStatus === 'decision_needed').length,
      gaps: programs.reduce((sum, program) => sum + program.openGapCount, 0),
      monitoring: programs.filter((program) => program.verificationPlanId || program.controlPlanMetricCount > 0).length,
      drifted: programs.filter((program) => program.attentionStatus === 'drifted').length,
    }),
    [programs],
  );

  return (
    <main className="min-h-screen bg-background" data-testid="blueprint-program-portfolio">
      <section className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-[1520px] flex-col gap-5 px-6 py-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-4xl">
              <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Strategy Studio
              </div>
              <h1 className="mt-2 font-serif text-[34px] font-semibold leading-tight text-foreground">
                Blueprints
              </h1>
              <p className="mt-2 max-w-3xl text-[15px] leading-6 text-muted-foreground">
                Governed blueprint programs across value creation, operating model, transformation,
                integration, turnaround, cost transformation, and growth.
              </p>
            </div>
            <Link
              href="/strategy-studio/blueprints/new"
              className="inline-flex h-9 w-fit items-center justify-center gap-2 rounded-md border border-primary bg-primary px-4 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              New Blueprint
            </Link>
          </div>

          <div className="rounded-lg border border-[hsl(var(--helix-primary-medium))] bg-[hsl(var(--helix-primary-light))] px-4 py-3">
            <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-primary">
              Helix signal
            </div>
            <div className="mt-1 text-[14px] leading-6 text-foreground">{deriveSignal(programs)}</div>
          </div>

          <div className="grid gap-3 md:grid-cols-5">
            <SummaryTile label="Active" value={summary.active} />
            <SummaryTile label="Decisions" value={summary.decisions} urgent={summary.decisions > 0} />
            <SummaryTile label="Evidence gaps" value={summary.gaps} urgent={summary.gaps > 0} />
            <SummaryTile label="Monitoring" value={summary.monitoring} />
            <SummaryTile label="Drifted" value={summary.drifted} urgent={summary.drifted > 0} />
          </div>
        </div>
      </section>

      <section className="mx-auto flex max-w-[1520px] flex-col gap-5 px-6 py-5">
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3 md:flex-row md:items-center md:justify-between">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search programs, mandates, owners, or recommendations"
              className="pl-9"
              aria-label="Search Blueprint Programs"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label="Filter by blueprint type"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value as TypeFilter)}
              options={[
                { value: 'all', label: 'All types' },
                ...Object.entries(BLUEPRINT_TYPE_LABELS).map(([value, label]) => ({ value, label })),
              ]}
              selectSize="sm"
            />
            <Select
              aria-label="Filter by state"
              value={stateFilter}
              onChange={(event) => setStateFilter(event.target.value as StateFilter)}
              options={[
                { value: 'all', label: 'All states' },
                { value: 'decision_needed', label: 'Decision needed' },
                { value: 'active', label: 'Active' },
                { value: 'drifted', label: 'Drifted' },
                { value: 'evidence_gap', label: 'Evidence gap' },
                { value: 'blocked', label: 'Blocked' },
              ]}
              selectSize="sm"
            />
            <div className="flex rounded-md border border-border bg-muted p-1" aria-label="View mode">
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={cn(
                  'inline-flex h-8 items-center gap-1 rounded px-2 text-[12px] font-medium',
                  viewMode === 'cards' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
                )}
              >
                <Grid3X3 className="h-3.5 w-3.5" />
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={cn(
                  'inline-flex h-8 items-center gap-1 rounded px-2 text-[12px] font-medium',
                  viewMode === 'list' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
                )}
              >
                <ListFilter className="h-3.5 w-3.5" />
                List
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="py-10 text-[14px] text-muted-foreground">
              Loading Blueprint Programs...
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="border-[hsl(var(--helix-eliminate-border))]">
            <CardContent className="flex items-start gap-3 py-5">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-[hsl(var(--helix-eliminate))]" />
              <div>
                <div className="text-[14px] font-semibold text-foreground">Blueprint Programs could not load.</div>
                <div className="mt-1 text-[13px] leading-5 text-muted-foreground">{error}</div>
              </div>
            </CardContent>
          </Card>
        ) : filteredPrograms.length === 0 ? (
          <Card>
            <CardContent className="py-10">
              <div className="text-[16px] font-semibold text-foreground">No Blueprint Programs match this view.</div>
              <div className="mt-2 max-w-2xl text-[14px] leading-6 text-muted-foreground">
                Clear filters or start a new blueprint from a strategic mandate. Programs appear here after
                Helix has a governed run, synthesis, decision posture, or control-plan binding to summarize.
              </div>
            </CardContent>
          </Card>
        ) : viewMode === 'cards' ? (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredPrograms.map((program) => (
              <ProgramCard key={program.programId} program={program} />
            ))}
          </div>
        ) : (
          <ProgramList programs={filteredPrograms} />
        )}
      </section>
    </main>
  );
}

function SummaryTile({ label, value, urgent = false }: { label: string; value: number; urgent?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-background px-4 py-3">
      <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className={cn('mt-1 font-serif text-[28px] font-semibold', urgent ? 'text-[hsl(var(--helix-defer))]' : 'text-foreground')}>
        {value}
      </div>
    </div>
  );
}

function ProgramCard({ program }: { program: BlueprintProgramProjectionDto }) {
  return (
    <Card interactive className="overflow-hidden">
      <CardHeader className="gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="accent">{typeLabel(program)}</Badge>
              <Badge variant={statusVariant(program.attentionStatus)}>
                {ATTENTION_LABELS[program.attentionStatus]}
              </Badge>
            </div>
            <CardTitle className="mt-3 font-serif text-[22px]">{program.title}</CardTitle>
          </div>
          <div className="text-right text-[12px] text-muted-foreground">
            <div>Next review</div>
            <div className="mt-1 font-semibold text-foreground">{formatDate(program.nextReviewAt)}</div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-[15px] leading-6 text-foreground">{program.headline}</p>
        <p className="mt-2 text-[13px] leading-5 text-muted-foreground">{program.executiveTakeaway}</p>

        <div className="mt-5 grid gap-3 sm:grid-cols-4">
          <ProgramMetric label="State" value={LIFECYCLE_LABELS[program.lifecycleState]} />
          <ProgramMetric label="Owner" value={ownerLabel(program)} />
          <ProgramMetric label="Open gaps" value={String(program.openGapCount)} urgent={program.openGapCount > 0} />
          <ProgramMetric label="Control metrics" value={String(program.controlPlanMetricCount)} />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <Link
            href={`/strategy-studio/blueprints/${encodeURIComponent(program.slug)}`}
            className="inline-flex items-center gap-2 text-[14px] font-semibold text-primary hover:underline"
          >
            Open program
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            href={buildWorkingHref(program)}
            className="text-[13px] font-medium text-muted-foreground hover:text-foreground hover:underline"
          >
            Working view
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgramMetric({ label, value, urgent = false }: { label: string; value: string; urgent?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className={cn('mt-1 truncate text-[13px] font-semibold', urgent ? 'text-[hsl(var(--helix-defer))]' : 'text-foreground')}>
        {value}
      </div>
    </div>
  );
}

function ProgramList({ programs }: { programs: BlueprintProgramProjectionDto[] }) {
  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-[minmax(280px,1.6fr)_160px_150px_130px_120px] border-b border-border bg-muted/40 px-4 py-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        <div>Program</div>
        <div>Type</div>
        <div>Attention</div>
        <div>Next</div>
        <div className="text-right">Open</div>
      </div>
      {programs.map((program) => (
        <div
          key={program.programId}
          className="grid grid-cols-[minmax(280px,1.6fr)_160px_150px_130px_120px] items-center border-b border-border px-4 py-4 last:border-b-0"
        >
          <div className="min-w-0">
            <div className="truncate text-[14px] font-semibold text-foreground">{program.title}</div>
            <div className="mt-1 truncate text-[13px] text-muted-foreground">{program.headline}</div>
          </div>
          <div className="text-[13px] text-muted-foreground">{typeLabel(program)}</div>
          <div>
            <Badge variant={statusVariant(program.attentionStatus)}>
              {ATTENTION_LABELS[program.attentionStatus]}
            </Badge>
          </div>
          <div className="text-[13px] text-muted-foreground">{formatDate(program.nextReviewAt)}</div>
          <div className="text-right">
            <Link
              href={`/strategy-studio/blueprints/${encodeURIComponent(program.slug)}`}
              className="inline-flex items-center justify-end gap-1 text-[13px] font-semibold text-primary hover:underline"
            >
              Open
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      ))}
    </Card>
  );
}
